import { useContext, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { AppContext } from '~/App';
import { useSaveNotificationPreferencesMutation } from '~/generated/graphql';
import {
  getBrowserTimeZone,
  getNotificationPrefsVisibility,
  nextMessagesChannels,
  normalizeNotificationSettings,
  type NotificationRole,
  type NotificationSpecifics,
  type StatusChangesKey,
  type UserNotificationSettingsV2,
} from '~/helpers/notificationPrefs';
import { saveNotificationPrefsChange } from '~/helpers/notificationPrefsSave';
import {
  applyReminderColumnSelectAll,
  applyReminderSectionSelectAll,
  applyStatusChangesColumnSelectAll,
  applyStatusChangesSectionSelectAll,
  applySummaryParentColumnSelectAll,
  applySummarySectionSelectAll,
  applySummaryStatusIncludeColumnSelectAll,
} from '~/helpers/notificationPrefsSelectAll';
import { Page } from '../user';
import { MethodsSection } from './notificationPrefs/MethodsSection';
import { RemindersSpecifics } from './notificationPrefs/RemindersSpecifics';
import { StatusChangesSpecifics } from './notificationPrefs/StatusChangesSpecifics';
import { SummariesSpecifics } from './notificationPrefs/SummariesSpecifics';

type Props = {
  setShowPage: (page: Page) => void;
};

export const NotificationsForm = ({ setShowPage }: Props) => {
  const { user: currentUser, revalidate } = useContext(AppContext);
  invariant(currentUser);

  const sendTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const initial = useMemo(
    () =>
      normalizeNotificationSettings(
        currentUser.notificationSettings,
        sendTimeZone
      ),
    [currentUser.notificationSettings, sendTimeZone]
  );

  const [settings, setSettings] = useState<UserNotificationSettingsV2>(initial);
  const [saving, setSaving] = useState(false);
  const [, saveMutation] = useSaveNotificationPreferencesMutation();

  const visibility = getNotificationPrefsVisibility(
    settings.methods,
    settings.specifics.jobSummaries.last24Hours
  );

  async function persist(
    paths: string | string[],
    next: UserNotificationSettingsV2
  ) {
    const pathList = Array.isArray(paths) ? paths : [paths];
    setSettings(next);
    if (pathList.length === 0) return;

    setSaving(true);
    try {
      // Refresh TZ on any prefs save (spec: browser/OS TZ)
      const withTz: UserNotificationSettingsV2 = {
        ...next,
        specifics: {
          ...next.specifics,
          jobSummaries: {
            ...next.specifics.jobSummaries,
            sendTimeZone: getBrowserTimeZone(),
          },
        },
      };
      if (
        withTz.specifics.jobSummaries.sendTimeZone !==
        next.specifics.jobSummaries.sendTimeZone
      ) {
        setSettings(withTz);
      }
      let anySaved = false;
      for (const path of pathList) {
        const result = await saveNotificationPrefsChange({
          path,
          value: withTz,
          save: (vars) => saveMutation(vars),
        });
        if (result.status === 'saved') {
          anySaved = true;
        }
      }
      if (anySaved) {
        await revalidate();
      }
    } finally {
      setSaving(false);
    }
  }

  function onMethodToggle(
    path:
      | 'messages.email'
      | 'messages.sms'
      | 'jobReminders.email'
      | 'jobReminders.sms'
      | 'jobSummaries.email'
      | 'statusChanges.email',
    nextValue: boolean
  ) {
    const next = structuredClone(settings);
    if (path.startsWith('messages.')) {
      const channel = path.endsWith('email') ? 'email' : 'sms';
      next.methods.messages = nextMessagesChannels(
        settings.methods.messages,
        channel,
        nextValue
      );
    } else if (path === 'jobReminders.email') {
      next.methods.jobReminders.email = nextValue;
    } else if (path === 'jobReminders.sms') {
      next.methods.jobReminders.sms = nextValue;
    } else if (path === 'jobSummaries.email') {
      next.methods.jobSummaries.email = nextValue;
    } else if (path === 'statusChanges.email') {
      next.methods.statusChanges.email = nextValue;
    }
    void persist(`methods.${path}`, next);
  }

  function onReminderToggle(
    timing: keyof NotificationSpecifics['jobReminders'],
    role: NotificationRole,
    nextValue: boolean
  ) {
    const next = structuredClone(settings);
    next.specifics.jobReminders[timing][role] = nextValue;
    void persist(`specifics.jobReminders.${timing}.${role}`, next);
  }

  function onSummaryRoleToggle(
    window: 'next24Hours' | 'last24Hours',
    role: NotificationRole,
    nextValue: boolean
  ) {
    const next = structuredClone(settings);
    next.specifics.jobSummaries[window][role] = nextValue;
    void persist(`specifics.jobSummaries.${window}.${role}`, next);
  }

  function onSummarySendTime(
    window: 'next24Hours' | 'last24Hours',
    sendTime: string
  ) {
    const next = structuredClone(settings);
    next.specifics.jobSummaries[window].sendTime = sendTime;
    void persist(`specifics.jobSummaries.${window}.sendTime`, next);
  }

  function onStatusIncludeToggle(
    status: keyof NotificationSpecifics['jobSummaries']['last24Hours']['statusChangesToInclude'],
    role: NotificationRole,
    nextValue: boolean
  ) {
    const next = structuredClone(settings);
    next.specifics.jobSummaries.last24Hours.statusChangesToInclude[status][
      role
    ] = nextValue;
    void persist(
      `specifics.jobSummaries.last24Hours.statusChangesToInclude.${status}.${role}`,
      next
    );
  }

  function onStatusSpecificsToggle(
    status: StatusChangesKey,
    role: NotificationRole,
    nextValue: boolean
  ) {
    const next = structuredClone(settings);
    next.specifics.statusChanges[status] = {
      ...next.specifics.statusChanges[status],
      [role]: nextValue,
    };
    void persist(`specifics.statusChanges.${status}.${role}`, next);
  }

  return (
    <form className='min-w-0 max-w-full'>
      <div className='flex min-w-0 flex-col gap-10 text-copy-alt'>
        <p className='text-secondary'>
          Specify which notifications you would like to receive and how you
          would like to receive them.
        </p>

        <MethodsSection
          methods={settings.methods}
          disabled={saving}
          onToggle={onMethodToggle}
        />

        {(visibility.jobRemindersSpecifics ||
          visibility.jobSummariesSpecifics) && (
          <section className='flex flex-col gap-8'>
            <h2 className='text-lg font-semibold text-primary'>
              Notification Specifics
            </h2>
            {visibility.jobRemindersSpecifics && (
              <RemindersSpecifics
                jobReminders={settings.specifics.jobReminders}
                disabled={saving}
                onToggle={onReminderToggle}
                onSectionSelectAll={(checked) => {
                  const { next, paths } = applyReminderSectionSelectAll(
                    settings,
                    checked
                  );
                  void persist(paths, next);
                }}
                onColumnSelectAll={(role, checked) => {
                  const { next, paths } = applyReminderColumnSelectAll(
                    settings,
                    role,
                    checked
                  );
                  void persist(paths, next);
                }}
              />
            )}
            {visibility.jobSummariesSpecifics && (
              <SummariesSpecifics
                jobSummaries={settings.specifics.jobSummaries}
                showStatusInclude={visibility.last24hStatusInclude}
                disabled={saving}
                onToggleRole={onSummaryRoleToggle}
                onSendTime={onSummarySendTime}
                onToggleStatusInclude={onStatusIncludeToggle}
                onSectionSelectAll={(checked) => {
                  const { next, paths } = applySummarySectionSelectAll(
                    settings,
                    checked
                  );
                  void persist(paths, next);
                }}
                onParentColumnSelectAll={(role, checked) => {
                  const { next, paths } = applySummaryParentColumnSelectAll(
                    settings,
                    role,
                    checked
                  );
                  void persist(paths, next);
                }}
                onStatusIncludeColumnSelectAll={(role, checked) => {
                  const { next, paths } =
                    applySummaryStatusIncludeColumnSelectAll(
                      settings,
                      role,
                      checked
                    );
                  void persist(paths, next);
                }}
              />
            )}
          </section>
        )}

        {visibility.statusChangesSpecifics && (
          <StatusChangesSpecifics
            statusChanges={settings.specifics.statusChanges}
            disabled={saving}
            onToggle={onStatusSpecificsToggle}
            onSectionSelectAll={(checked) => {
              const { next, paths } = applyStatusChangesSectionSelectAll(
                settings,
                checked
              );
              void persist(paths, next);
            }}
            onColumnSelectAll={(role, checked) => {
              const { next, paths } = applyStatusChangesColumnSelectAll(
                settings,
                role,
                checked
              );
              void persist(paths, next);
            }}
          />
        )}
      </div>

      {/* Pre-existing: checks email instead of phone — out of scope */}
      {!currentUser?.email && (
        <p className='text-red pt-2 text-sm font-light'>
          You need a valid mobile phone number to receive SMS notifications.
          Click{' '}
          <span
            onClick={() => setShowPage('personal')}
            className='cursor-pointer text-blue underline'
          >
            here{' '}
          </span>
          to add one.
        </p>
      )}
    </form>
  );
};
